/**
 * GiftMotor auth contract — DOM-free and provider-free (portability ruling:
 * this layer must survive a future native shell unchanged).
 *
 * Session doctrine (B-AUTH, accepted 2026-08-29): access/ID tokens live in
 * MEMORY only; the refresh credential exists only inside the broker's
 * HttpOnly cookie. Nothing in this layer may touch persistent storage.
 */

export interface AuthTokens {
  readonly accessToken: string;
  readonly idToken: string;
  /** Seconds until the access token expires. */
  readonly expiresIn: number;
}

/** One SRP sign-in result: tokens plus the one-shot refresh credential. */
export interface SignInResult {
  readonly tokens: AuthTokens;
  /**
   * Handed to SessionBroker.adopt exactly once, then destroyed. Never stored,
   * never logged, never shown.
   */
  readonly refreshToken: string;
}

/** Identity provider (Cognito SRP beneath GiftMotor-owned UI). */
export interface AuthProvider {
  /** Direct SRP sign-in — the password goes to the provider only. */
  signIn(email: string, password: string): Promise<SignInResult>;
  /** Self-signup; the provider emails a verification code. */
  signUp(email: string, password: string): Promise<void>;
  /** Confirms signup with the emailed code. */
  confirmSignUp(email: string, code: string): Promise<void>;
  resendConfirmationCode(email: string): Promise<void>;
  /** Starts password recovery; the provider emails a code. */
  forgotPassword(email: string): Promise<void>;
  /** Completes recovery with the emailed code and the new password. */
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void>;
}

/** The B-AUTH session broker (HttpOnly cookie authority). */
export interface SessionBroker {
  /** Hands the refresh credential to the broker once; cookie comes back. */
  adopt(refreshToken: string): Promise<void>;
  /** Cookie → fresh tokens, or undefined when no session exists. */
  refresh(): Promise<AuthTokens | undefined>;
  /** Revokes at the provider and clears the cookie. Always succeeds. */
  signOut(): Promise<void>;
}

export interface MeProfile {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
}

/** Typed errors the UI can message on. Anything else renders generically. */
export type AuthErrorCode =
  | 'INVALID_CREDENTIALS'
  | 'USER_NOT_CONFIRMED'
  | 'CODE_MISMATCH'
  | 'CODE_EXPIRED'
  | 'PASSWORD_POLICY'
  | 'EMAIL_TAKEN'
  | 'RATE_LIMITED'
  | 'NEW_PASSWORD_REQUIRED'
  | 'SUSPENDED'
  | 'NETWORK'
  | 'UNKNOWN';

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}
